import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadCenter, loadPublicMatch, subscribeCenter, type CenterData } from '../services/publicMatchCenter';

export function usePublicMatchCenter(id: string) {
  const match = useQuery({ queryKey: ['public-match-center-match', id], queryFn: () => loadPublicMatch(id), retry: 1, refetchInterval: 5000 });
  const [data, setData] = useState<CenterData>();
  const [connection, setConnection] = useState('connecting');
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const abort = new AbortController();
    let current: CenterData | undefined;
    let busy = false;
    let pending = false;
    let close: (() => void) | undefined;
    let connected = false;
    setData(undefined);
    setFailed(false);
    setConnection('connecting');
    const refresh = async () => {
      if (abort.signal.aborted) return;
      if (busy) { pending = true; return; }
      busy = true;
      try {
        const next = await loadCenter(id, abort.signal, current);
        if (abort.signal.aborted) return;
        current = next;
        setData(next);
        setFailed(false);
        setConnection(connected ? 'live' : 'reconnecting');
        if (!close) close = subscribeCenter(id, next.snapshot.revision, () => { void refresh(); }, online => {
          if (abort.signal.aborted) return;
          connected = online;
          setConnection(online ? 'live' : 'reconnecting');
        });
      } catch {
        if (!abort.signal.aborted) { setFailed(true); setConnection('stale'); }
      } finally {
        busy = false;
        if (pending && !abort.signal.aborted) { pending = false; void refresh(); }
      }
    };
    const visible = () => { if (document.visibilityState === 'visible') void refresh(); };
    void refresh();
    const poll = window.setInterval(visible, 15000);
    const tick = window.setInterval(() => setNow(Date.now()), 500);
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    return () => { abort.abort(); close?.(); clearInterval(poll); clearInterval(tick); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [id, retry]);
  const snapshot = data?.snapshot;
  const state = snapshot?.state;
  const duration = snapshot ? (state!.currentPeriod > snapshot.configuration.regularPeriodCount ? snapshot.configuration.overtimePeriodDurationMs : snapshot.configuration.regularPeriodDurationMs) : 1800000;
  const serverAdvance = state?.clockRunning && state.clockAnchorAt ? Math.max(0, Date.parse(snapshot!.serverTime) - Date.parse(state.clockAnchorAt)) : 0;
  const advance = serverAdvance + (state?.clockRunning && connection !== 'stale' ? Math.max(0, now - (data?.receivedAt ?? now)) : 0);
  const elapsed = Math.min(duration, (state?.periodElapsedAnchorMs ?? 0) + advance);
  const effective = (state?.effectiveElapsedAnchorMs ?? 0) + Math.max(0, elapsed - (state?.periodElapsedAnchorMs ?? 0));
  return { match, data, connection, failed, elapsed, effective, retry: () => { setRetry(v => v + 1); void match.refetch(); } };
}
